import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Customer, InvitationDetails } from '../types';
import {
  customerPickerOptions,
  invitationEmail,
  invitationName,
  invitationPickerOptions,
  isVerifiedCustomer,
  pendingCustomerInvitations
} from '../utils/customerVerification';

interface ProjectCustomerPickerProps {
  id: string;
  customers: Customer[];
  // Customer invitations not yet accepted; listed as disabled options.
  invitations?: InvitationDetails[];
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  search: string;
  onSearchChange: (search: string) => void;
  error?: string;
  customerListPath: string;
}

// Shown above this many customers, matching the picker's earlier behaviour.
const SEARCH_THRESHOLD = 5;

/**
 * Customer picker for project creation. Every customer is listed so the company
 * can see who exists, but only verified customers are selectable — the others
 * are native disabled options, which mouse, keyboard and screen readers all
 * treat as unavailable. The API enforces the same rule on submit. Invitations
 * that have not been accepted yet are listed the same way.
 */
export function ProjectCustomerPicker({
  id,
  customers,
  invitations = [],
  value,
  onChange,
  search,
  onSearchChange,
  error,
  customerListPath
}: ProjectCustomerPickerProps) {
  const { t } = useTranslation();

  const options = useMemo(
    () => customerPickerOptions(customers, search, value),
    [customers, search, value]
  );
  const verifiedCount = useMemo(
    () => customers.filter(isVerifiedCustomer).length,
    [customers]
  );
  const pendingInvitations = useMemo(
    () => pendingCustomerInvitations(invitations, customers),
    [invitations, customers]
  );
  const invitationOptions = useMemo(
    () => invitationPickerOptions(pendingInvitations, search),
    [pendingInvitations, search]
  );
  const totalCount = customers.length + pendingInvitations.length;
  const unverifiedCount = totalCount - verifiedCount;
  const hintId = `${id}-unverified-hint`;
  const errorId = `${id}-error`;

  if (totalCount === 0) {
    return (
      <div className="alert alert-warning mb-0" role="status">
        <i className="feather-alert-triangle me-2" aria-hidden="true"></i>
        {t('projects.form.noCustomers')}
        <div className="mt-2">
          <Link to={customerListPath} className="alert-link">
            {t('projects.form.goToCustomerList')}
          </Link>
        </div>
      </div>
    );
  }

  const describedBy = [unverifiedCount > 0 ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {verifiedCount === 0 && (
        <div className="alert alert-warning mb-2" role="status">
          <i className="feather-alert-triangle me-2" aria-hidden="true"></i>
          {t('projects.form.noVerifiedCustomers')}
        </div>
      )}
      {totalCount > SEARCH_THRESHOLD && (
        <>
          <input
            type="text"
            className="form-control mb-2"
            placeholder={t('projects.form.searchCustomer')}
            aria-label={t('projects.form.searchCustomer')}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
          {search.trim() && (
            <small className="text-muted d-block mb-2" aria-live="polite">
              {t('projects.form.customerSearchMatches', {
                count: options.length + invitationOptions.length,
                total: totalCount
              })}
            </small>
          )}
        </>
      )}
      <select
        id={id}
        name="customerId"
        className={`form-select ${error ? 'is-invalid' : ''}`}
        value={value}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      >
        <option value="">{t('projects.form.selectCustomer')}</option>
        {options.map(customer => {
          const verified = isVerifiedCustomer(customer);
          return (
            <option key={customer.id} value={customer.id} disabled={!verified}>
              {t('projects.form.customerOption', {
                number: customer.customerNumber,
                name: customer.name,
                email: customer.email,
                status: verified
                  ? t('customers.status.verified')
                  : t('projects.form.customerStatusNotVerified')
              })}
            </option>
          );
        })}
        {invitationOptions.map(invitation => (
          <option key={`invitation-${invitation.id}`} value={`invitation:${invitation.id}`} disabled>
            {t('projects.form.invitationOption', {
              name: invitationName(invitation),
              email: invitationEmail(invitation),
              status: t('projects.form.customerStatusInvitationPending')
            })}
          </option>
        ))}
      </select>
      {error && (
        <div id={errorId} className="invalid-feedback d-block">
          {error}
        </div>
      )}
      {unverifiedCount > 0 && (
        <div id={hintId} className="form-text">
          {t('projects.form.unverifiedCustomersHint', { count: unverifiedCount })}
          <div className="mt-1">
            <Link to={customerListPath}>{t('projects.form.goToCustomerList')}</Link>
          </div>
        </div>
      )}
    </>
  );
}
